// packages/frontend/src/components/shared/Modal.tsx

import { useEffect, useRef, Fragment, ReactNode } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'

export interface ModalProps {
  /** Modal open state */
  isOpen: boolean

  /** Close handler */
  onClose: () => void

  /** Modal title */
  title?: string

  /** Modal body content */
  children: ReactNode

  /** Footer content (typically buttons) */
  footer?: ReactNode

  /** Modal size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'

  /** Modal type */
  type?: 'dialog' | 'confirmation' | 'drawer'

  /** Prevent closing on overlay click */
  preventClose?: boolean

  /** Show close X button */
  showCloseButton?: boolean

  /** Accessible description for screen readers */
  ariaDescription?: string

  /** ID for aria-describedby */
  descriptionId?: string
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  type = 'dialog',
  preventClose = false,
  showCloseButton = true,
  ariaDescription,
  descriptionId = 'modal-description'
}) => {
  const initialFocusRef = useRef<HTMLButtonElement>(null)

  // Close on ESC key (Headless UI handles this, but keeping for explicit control)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !preventClose) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose, preventClose])

  // Announce modal opening to screen readers
  useEffect(() => {
    if (isOpen) {
      // Announce to screen readers that modal has opened
      const announcement = document.createElement('div')
      announcement.setAttribute('role', 'status')
      announcement.setAttribute('aria-live', 'polite')
      announcement.className = 'sr-only'
      announcement.textContent = `${title || 'Dialog'} opened`
      document.body.appendChild(announcement)

      // Remove after announcement
      const timer = setTimeout(() => {
        if (document.body.contains(announcement)) {
          document.body.removeChild(announcement)
        }
      }, 1000)

      return () => {
        clearTimeout(timer)
        if (document.body.contains(announcement)) {
          document.body.removeChild(announcement)
        }
      }
    }
  }, [isOpen, title])

  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
    full: 'max-w-7xl'
  }

  // Type-specific positioning
  const typeClasses = {
    dialog: 'items-center',
    confirmation: 'items-center',
    drawer: 'items-end'
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={preventClose ? () => {} : onClose}
        initialFocus={initialFocusRef}
        aria-describedby={ariaDescription ? descriptionId : undefined}
      >
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-40"
            aria-hidden="true"
          />
        </Transition.Child>

        {/* Modal positioning */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className={`flex min-h-full justify-center p-4 ${typeClasses[type]}`}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full ${sizeClasses[size]} transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all`}
              >
                {/* Header */}
                {(title || showCloseButton) && (
                  <div className="flex items-center justify-between mb-4">
                    {title && (
                      <Dialog.Title
                        as="h2"
                        className="text-lg font-semibold text-gray-900"
                      >
                        {title}
                      </Dialog.Title>
                    )}
                    {showCloseButton && (
                      <button
                        ref={initialFocusRef}
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors rounded p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        aria-label="Close modal"
                        type="button"
                      >
                        <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                )}

                {/* Description for screen readers */}
                {ariaDescription && (
                  <p id={descriptionId} className="sr-only">
                    {ariaDescription}
                  </p>
                )}

                {/* Body */}
                <div className="mt-2">
                  {children}
                </div>

                {/* Footer */}
                {footer && (
                  <div className="mt-6 flex justify-end gap-3" role="group" aria-label="Modal actions">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
